import React from 'react';
import PropTypes from 'prop-types';
import CurrencyHelper from '../../Helpers/CurrencyHelper';
import DollarsAndCents from '../DollarsAndCents';
import BasicCoverage from './BasicCoverage';
import GuestsVendors from './GuestsVendors';
import Dates from '../Dates';
import { Button } from '@jauntin/react-ui';

const SummaryTable = ({
  coverage,
  basicCoverageConfirmed,
  eventDates,
  eventFrequency,
  daysOfWeek,
  eventTotalGuests,
  aboutEventConfirmed,
  eventPerformersCount,
  eventGoods,
  eventFood,
  eventExhibition,
  sellingLiquor,
  basicCoverageAmount,
  liquor,
  subtotal,
  federalSurcharge,
  stateSurcharge,
  venueState,
  terrorism,
  damageToRentedPropertyPrice,
  damageToRentedProperty,
  isGettingCertHolders,
  handleShow,
}) => {
  let taxType = 'State Tax';
  if (venueState.toUpperCase() === 'KY') {
    taxType = 'KY Municipal Tax';
  }
  if (venueState.toUpperCase() === 'FL') {
    taxType = 'State Surcharge';
  }
  return (
    <div className="summary__table">
      <div className="summary__additionalCertHolders">
        <div>This policy has additional certificate holders.</div>
        <br />
        {isGettingCertHolders ? (
          <>
            <span>
              <i className="fas fa-circle-notch fa-spin text-secondary spinner" />
            </span>
            <span className="text-secondary mx-2">
              loading certificate holders...
            </span>
          </>
        ) : (
          <Button
            className="summary__link"
            text="Preview my certificate language"
            onClick={() => {
              handleShow();
            }}
          />
        )}
      </div>
      <div className="form-row">
        <div className="col">
          <div className="d-flex justify-content-between">
            <div className="font-weight-bold">Basic Coverage</div>
            <div className="font-weight-bold">
              <DollarsAndCents
                currency={new CurrencyHelper()
                  .setValue(basicCoverageAmount)
                  .convertFromInt()}
              />
            </div>
          </div>
          <div className="col p-0 m-0 list-unstyled list-font">
            <BasicCoverage
              confirmed={basicCoverageConfirmed}
              coverage={coverage}
            />
          </div>
          <div className="mb-3">
            Includes:
            {!sellingLiquor && <div>Host Liquor</div>}
            <div>Damage to Rented Premises up to $250,000</div>
          </div>
          <ul className="p-0 m-0 list-unstyled list-font">
            {aboutEventConfirmed && (
              <>
                <Dates
                  dates={eventDates}
                  type={eventFrequency}
                  frequency={daysOfWeek}
                />
              </>
            )}
            <GuestsVendors
              aboutEventConfirmed={aboutEventConfirmed}
              eventTotalGuests={eventTotalGuests}
              eventPerformersCount={eventPerformersCount}
              eventGoods={eventGoods}
              eventFood={eventFood}
              eventExhibition={eventExhibition}
            />
          </ul>
        </div>
      </div>
      <div className="form-row mt-3">
        <div className="col">
          <div className="font-weight-bold mb-0">Additional Coverages</div>
          <ul className="p-0 m-0 list-unstyled list-font">
            {terrorism && (
              <li className="d-flex justify-content-between">
                <span>Terrorism Coverage (included)</span>
                <span className="font-weight-bold">
                  <DollarsAndCents
                    currency={new CurrencyHelper().setValue(0)}
                  />
                </span>
              </li>
            )}
            {sellingLiquor && (
              <li className="d-flex justify-content-between">
                <span>Liquor Liability</span>
                <span className="font-weight-bold">
                  <DollarsAndCents
                    currency={new CurrencyHelper()
                      .setValue(liquor)
                      .convertFromInt()}
                  />
                </span>
              </li>
            )}
            {damageToRentedProperty && (
              <li className="d-flex justify-content-between">
                <span>
                  Damage to Rented Premises limit&nbsp;increase to $1 million
                </span>
                <span className="font-weight-bold">
                  <DollarsAndCents
                    currency={new CurrencyHelper()
                      .setValue(damageToRentedPropertyPrice)
                      .convertFromInt()}
                  />
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="form-row border-top list-font pt-2 mt-1 mb-1">
        <div className="col">Subtotal</div>
        <div className="col-auto font-weight-bold">
          <DollarsAndCents
            currency={new CurrencyHelper().setValue(subtotal).convertFromInt()}
          />
        </div>
      </div>
      {federalSurcharge > 0 && (
        <div className="form-row list-font mb-1">
          <div className="col">
            <span className="font-italic">State Surcharge</span>
          </div>
          <div className="col-auto font-weight-bold">
            <DollarsAndCents
              currency={new CurrencyHelper()
                .setValue(federalSurcharge)
                .convertFromInt()}
            />
          </div>
        </div>
      )}
      {stateSurcharge > 0 && (
        <div className="form-row list-font mb-1">
          <div className="col font-italic">
            <span>{taxType}</span>
          </div>
          <div className="col-auto font-weight-bold">
            <DollarsAndCents
              currency={new CurrencyHelper()
                .setValue(stateSurcharge)
                .convertFromInt()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

SummaryTable.propTypes = {
  coverage: PropTypes.string.isRequired,
  basicCoverageConfirmed: PropTypes.bool.isRequired,
  eventDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  eventFrequency: PropTypes.string.isRequired,
  daysOfWeek: PropTypes.arrayOf(PropTypes.number).isRequired,
  eventTotalGuests: PropTypes.string.isRequired,
  aboutEventConfirmed: PropTypes.bool.isRequired,
  eventPerformersCount: PropTypes.string.isRequired,
  eventGoods: PropTypes.string.isRequired,
  eventFood: PropTypes.string.isRequired,
  eventExhibition: PropTypes.string.isRequired,
  sellingLiquor: PropTypes.bool.isRequired,
  terrorism: PropTypes.bool.isRequired,
  damageToRentedProperty: PropTypes.bool.isRequired,
  damageToRentedPropertyPrice: PropTypes.number.isRequired,
  basicCoverageAmount: PropTypes.number.isRequired,
  liquor: PropTypes.number.isRequired,
  subtotal: PropTypes.number.isRequired,
  federalSurcharge: PropTypes.number.isRequired,
  stateSurcharge: PropTypes.number.isRequired,
  venueState: PropTypes.string.isRequired,
  isGettingCertHolders: PropTypes.bool.isRequired,
  handleShow: PropTypes.func.isRequired,
};

export default SummaryTable;
